// Потоковое воспроизведение аудио-ответа агента в реальном времени.
// Поддерживает два формата входящих чанков (config.audio.recvFormat):
//   - 'pcm16'   → сырой Int16 PCM, планируется встык через Web Audio (мин. задержка).
//   - 'encoded' → сжатый контейнер (opus/mp3/aac), декодируется decodeAudioData.
// Прерывание (barge-in): flush() мгновенно глушит очередь и останавливает звук.

import type { AudioRecvFormat } from '../config'

export interface AudioPlayerOptions {
  sampleRate: number
  format: AudioRecvFormat
  /** Текущий уровень воспроизведения 0..1 — можно показывать «агент говорит». */
  onLevel?: (level: number) => void
}

export class AudioPlayer {
  private ctx: AudioContext | null = null
  private opts: AudioPlayerOptions
  private nextStartTime = 0
  private active = new Set<AudioBufferSourceNode>()
  private generation = 0 // увеличивается на flush — старые декоды отбрасываются
  private pendingDecodes = 0 // чанки, которые ещё декодируются
  private idleCallbacks: Array<() => void> = []

  constructor(opts: AudioPlayerOptions) {
    this.opts = opts
  }

  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const Ctx: typeof AudioContext =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.ctx = new Ctx({ sampleRate: this.opts.format === 'pcm16' ? this.opts.sampleRate : undefined })
    }
    return this.ctx
  }

  /** Должно вызываться в пользовательском жесте (тап), чтобы разблокировать звук на iOS. */
  async unlock(): Promise<void> {
    const ctx = this.ensureCtx()
    if (ctx.state === 'suspended') await ctx.resume()
    // iOS: одного resume мало — проигрываем беззвучный буфер в самом жесте,
    // иначе ПЕРВОЕ воспроизведение (первый ответ агента) не звучит.
    try {
      const buffer = ctx.createBuffer(1, 1, 22050)
      const src = ctx.createBufferSource()
      src.buffer = buffer
      src.connect(ctx.destination)
      src.start(0)
    } catch {
      /* noop */
    }
  }

  async enqueue(chunk: ArrayBuffer): Promise<void> {
    // Считаем декод СИНХРОННО (до await), чтобы whenIdle, вызванный сразу после
    // enqueue, видел незавершённую работу и не сработал преждевременно.
    this.pendingDecodes++
    const gen = this.generation
    try {
      const ctx = this.ensureCtx()
      if (ctx.state === 'suspended') await ctx.resume()

      let buffer: AudioBuffer
      if (this.opts.format === 'pcm16') {
        buffer = this.pcm16ToBuffer(ctx, chunk)
      } else {
        try {
          buffer = await ctx.decodeAudioData(chunk.slice(0))
        } catch {
          return // битый/неполный чанк — пропускаем
        }
      }

      if (gen !== this.generation) return // был flush, пока декодировали

      const src = ctx.createBufferSource()
      src.buffer = buffer

      const gain = ctx.createGain()
      src.connect(gain)
      gain.connect(ctx.destination)

      const now = ctx.currentTime
      const startAt = Math.max(now, this.nextStartTime)
      src.start(startAt)
      this.nextStartTime = startAt + buffer.duration

      this.active.add(src)
      src.onended = () => {
        this.active.delete(src)
        this.checkIdle()
      }

      if (this.opts.onLevel) this.opts.onLevel(0.6)
    } finally {
      this.pendingDecodes--
      this.checkIdle()
    }
  }

  /** Вызвать колбэк, когда вся очередь доиграла (или сразу, если уже тихо). */
  whenIdle(cb: () => void): void {
    if (this.pendingDecodes === 0 && this.active.size === 0) cb()
    else this.idleCallbacks.push(cb)
  }

  private checkIdle(): void {
    if (this.pendingDecodes === 0 && this.active.size === 0) {
      const cbs = this.idleCallbacks
      this.idleCallbacks = []
      cbs.forEach((cb) => cb())
    }
  }

  /** Мгновенно остановить воспроизведение и очистить очередь (прерывание). */
  flush(): void {
    this.generation++
    this.idleCallbacks = [] // отменяем ожидающих «доиграл» — этот путь прерван
    for (const src of this.active) {
      try {
        src.stop()
      } catch {
        /* noop */
      }
    }
    this.active.clear()
    this.nextStartTime = this.ctx ? this.ctx.currentTime : 0
    this.opts.onLevel?.(0)
  }

  get isPlaying(): boolean {
    return this.active.size > 0
  }

  close(): void {
    this.flush()
    this.ctx?.close().catch(() => {})
    this.ctx = null
  }

  private pcm16ToBuffer(ctx: AudioContext, chunk: ArrayBuffer): AudioBuffer {
    const view = new Int16Array(chunk)
    const buffer = ctx.createBuffer(1, view.length, this.opts.sampleRate)
    const out = buffer.getChannelData(0)
    for (let i = 0; i < view.length; i++) out[i] = view[i] / 0x8000
    return buffer
  }
}
